import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { LegalMarkdown } from "@/features/legal/components/legal-markdown";
import { LegalHubPage } from "@/features/legal/components/legal-page";
import { legalFacts } from "@/features/legal/utils/legal-facts";
import { pageMeta } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/(public)/legal/")({
  // Out of search until `legalEntity` holds the real company details.
  staticData: { sitemap: false },
  head: () => ({ meta: pageMeta({ title: m.legal_hub_title(), description: m.legal_hub_lede() }) }),
  loader: () => ({ crumb: m.legal_hub_title() }),
  component: LegalPage,
});

const documents = () =>
  [
    { to: "/terms", title: m.auth_legal_terms(), description: m.legal_terms_description() },
    { to: "/privacy", title: m.auth_legal_privacy(), description: m.legal_privacy_description() },
    { to: "/cookies", title: m.legal_cookies_title(), description: m.legal_cookies_description() },
    { to: "/consumer-health-data", title: m.legal_health_title(), description: m.legal_health_description() },
    { to: "/content-policy", title: m.legal_content_title(), description: m.legal_content_description() },
    { to: "/withdrawal", title: m.legal_withdrawal_title(), description: m.legal_withdrawal_description() },
  ] as const;

function LegalPage() {
  const { operator, address, registration, email } = legalFacts();

  return (
    <LegalHubPage>
      <ul className="divide-y divide-border rounded-3xl border border-border bg-background">
        {documents().map((document) => (
          <li key={document.to}>
            <Link
              to={document.to}
              className="group flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:text-primary md:px-6"
            >
              <span className="min-w-0">
                <span className="block text-[15px] font-medium">{document.title}</span>
                <span className="mt-0.5 block text-[13px] text-pretty text-muted-foreground">{document.description}</span>
              </span>
              <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          </li>
        ))}
      </ul>
      <section className="rounded-3xl border border-border bg-background px-5 py-5 md:px-6">
        <h2 className="text-[15px] font-medium">{m.legal_hub_operator_title()}</h2>
        <LegalMarkdown
          className="mt-2 text-[13px] leading-6 [&_p]:my-0"
          source={[operator, address, registration, email].join("\n\n")}
        />
      </section>
      <section className="rounded-3xl border border-border bg-background px-5 py-5 md:px-6">
        <h2 className="text-[15px] font-medium">{m.legal_hub_credits_title()}</h2>
        <LegalMarkdown className="mt-2 text-[13px] leading-6 [&_p]:my-0" source={m.legal_hub_credits_body()} />
      </section>
    </LegalHubPage>
  );
}
